import moment from 'moment-timezone'

export const SERVER_DATETIME_FMT = 'YYYY-MM-DD HH:mm:ss';

export const detectTimezone = () => {
  var tz = moment.tz.guess();
  return moment.tz(tz).format('ZZ')
}

const isValidIanaTz = (tzIana?: string): tzIana is string => {
  return !!tzIana && !!moment.tz.zone(tzIana);
}

// NOTE on contract:
// The API sends/receives timestamps as naive strings in SERVER_DATETIME_FMT.
// Mobile app logs show it interprets these as local wall-clock time (per simulation/server timezone)
// and then converts to UTC when needed.
// Therefore, we must *not* pre-convert these values to UTC on the admin UI side,
// otherwise we double-apply offsets when Postgres/server interprets them.

export const parseServerWallClock = (date: any, tzIana?: string) => {
  if (!date) return null;

  if (date instanceof Date) {
    const m = moment(date);
    return m.isValid() ? m : null;
  }

  if (typeof date === 'string') {
    if (isValidIanaTz(tzIana)) {
      const zoned = moment.tz(date, SERVER_DATETIME_FMT, true, tzIana);
      if (zoned.isValid()) return zoned;
    }

    // Fallback: parse as a naive local datetime
    const m1 = moment(date, SERVER_DATETIME_FMT, true);
    if (m1.isValid()) return m1;

    const m2 = moment(date, moment.ISO_8601, true);
    return m2.isValid() ? m2 : null;
  }

  const m = moment(date as any);
  return m.isValid() ? m : null;
}

// Convert server timestamp -> datetime-local string for the input.
// If tzIana is provided, show the wall-clock time in that timezone.
export const serverToLocalDate = (date: any, tzIana?: string): string => {
  const wallClock = parseServerWallClock(date, tzIana);
  if (!wallClock) return "";
  const zoned = isValidIanaTz(tzIana) ? wallClock.clone().tz(tzIana) : wallClock.clone();
  return zoned.format(moment.HTML5_FMT.DATETIME_LOCAL);
}

// Convert datetime-local string -> server timestamp string.
// If tzIana is provided, interpret input as wall-clock time in that timezone and send it as-is
// (no UTC conversion).
export const localToServerDate = (date: any, tzIana?: string): string => {
  if (!date) return "";

  if (typeof date === 'string') {
    const parsed = isValidIanaTz(tzIana)
      ? moment.tz(date, moment.HTML5_FMT.DATETIME_LOCAL, true, tzIana)
      : moment(date, moment.HTML5_FMT.DATETIME_LOCAL, true);

    if (!parsed.isValid()) return "";
    return parsed.format(SERVER_DATETIME_FMT);
  }

  const m = moment(date as any);
  return m.isValid() ? m.format(SERVER_DATETIME_FMT) : "";
}

export const getDuration = (first_date: any, second_date: any, time_unit: any, tzIana?: string): number => {
  const first = parseServerWallClock(first_date, tzIana);
  const second = parseServerWallClock(second_date, tzIana);
  if (!first || !second) return 0;

  const dmin = second.diff(first, 'minutes');
  const dunit = unitTimeConverter('minute', dmin, time_unit);
  return dunit;
}

export const generateDate = (dayOffset?: number) => dayOffset ? moment().add(dayOffset, 'days').format(SERVER_DATETIME_FMT) : moment().format(SERVER_DATETIME_FMT)

export const getTimeZonesList = () => {
  return moment.tz.names();
};

export const gmtFormat = (value: any) => {
  const date = moment().tz(value).format();
  const gmt = date.slice(19).replace(":", "");

  // Handle UTC timezone which returns 'Z' instead of numeric offset
  // Convert 'Z' to '+0000' for backend compatibility
  const normalizedGmt = gmt === 'Z' ? '+0000' : gmt;

  return normalizedGmt;
};

export const unitTimeConverter = (currentUnitTime: any, unitAmount: any, unitTypeToConvert: any) => {
  const hoursInDay = 24;
  const minutesInHour = 60; // also seconds in a minute.
  const minutesInDay = 1440;
  const secondsInHour = 3600;
  const secondsInDay = 86400;

  if (currentUnitTime === "day") {
    if (unitTypeToConvert === "day") {
      return unitAmount;
    }
    if (unitTypeToConvert === "hour") {
      return unitAmount * hoursInDay;
    }
    if (unitTypeToConvert === "minute") {
      return unitAmount * minutesInDay;
    }
    if (unitTypeToConvert === "second") {
      return unitAmount * secondsInDay;
    }
  } else if (currentUnitTime === "hour") {
    if (unitTypeToConvert === "day") {
      return unitAmount / hoursInDay;
    }
    if (unitTypeToConvert === "hour") {
      return unitAmount;
    }
    if (unitTypeToConvert === "minute") {
      return unitAmount * minutesInHour;
    }
    if (unitTypeToConvert === "second") {
      return unitAmount * secondsInHour;
    }
  } else if (currentUnitTime === "minute") {
    if (unitTypeToConvert === "day") {
      return unitAmount / minutesInDay;
    }
    if (unitTypeToConvert === "hour") {
      return unitAmount / minutesInHour;
    }
    if (unitTypeToConvert === "minute") {
      return unitAmount;
    }
    if (unitTypeToConvert === "second") {
      return unitAmount * minutesInHour;
    }
  } else if (currentUnitTime === "second") {
    if (unitTypeToConvert === "day") {
      return unitAmount / secondsInDay;
    }
    if (unitTypeToConvert === "hour") {
      return unitAmount / secondsInHour;
    }
    if (unitTypeToConvert === "minute") {
      return unitAmount / minutesInHour;
    }
    if (unitTypeToConvert === "second") {
      return unitAmount;
    }
  }
  return;
};
